import React, { useState } from 'react';
import { useServicesManager } from '@/hooks/useServicesManager';
import { useServiceFormState } from './services/ServiceFormState';
import { useServiceValidation } from './services/ServiceValidation';
import { ServicesHeader } from './services/ServicesHeader';
import { ServicesContent } from './services/ServicesContent';
import { NewServiceForm } from './services/NewServiceForm';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  description: string | null;
  session_instructions: string | null;
  price: number;
  duration_minutes: number;
  formats_available: string[];
  is_active: boolean;
  currency: string;
  service_photo: string | null;
  is_free_session: boolean;
  parental_consent_required?: boolean;
  adults_only?: boolean;
  created_at: string;
  updated_at: string;
  custom_therapy_tags?: string[];
  service_therapy_tags?: ServiceTherapyTag[];
}

interface ServicesManagerProps {
  userId: string;
}

export const ServicesManager: React.FC<ServicesManagerProps> = ({ userId }) => {
  const { services, isLoading, isSubmitting, addService, updateService, toggleServiceActive, deleteService, fetchServices } = useServicesManager(userId);
  const {
    newService,
    selectedFormats,
    setNewService,
    setSelectedFormats,
    resetForm,
    handleFormatChange,
    handleInputChange,
    handleSelectChange,
    handleToggleActive,
    handleToggleFreeSession,
    handleToggleParentalConsent,
    handleToggleAdultsOnly,
  } = useServiceFormState();
  
  const { validateForm } = useServiceValidation();
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const [editingService, setEditingService] = useState<Service | null>(null);

  const handleEdit = (service: Service) => {
    setEditingService(service);
    const hours = Math.floor(service.duration_minutes / 60);
    const minutes = service.duration_minutes % 60;
    
    // Extract therapy tag IDs from service_therapy_tags
    const therapyTagIds = service.service_therapy_tags?.map(tag => tag.therapy_modality.id) || [];
    
    setNewService({
      service_name: service.service_name,
      description: service.description || '',
      session_instructions: service.session_instructions || '',
      price: service.price.toString(),
      duration_minutes: service.duration_minutes,
      duration_hours: hours,
      duration_minutes_part: minutes,
      therapy_tags: therapyTagIds,
      custom_therapy_tags: service.custom_therapy_tags || [],
      formats_available: service.formats_available,
      currency: service.currency,
      is_active: service.is_active,
      is_free_session: service.is_free_session,
      parental_consent_required: service.parental_consent_required || false,
      adults_only: service.adults_only || false,
    });
    
    // Set selected formats
    const formatState: Record<string, boolean> = {
      in_person: false,
      virtual: false,
    };
    service.formats_available.forEach(format => {
      formatState[format] = true;
    });
    setSelectedFormats(formatState);
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate therapy tags (standard OR custom)
    if (newService.therapy_tags.length === 0 && newService.custom_therapy_tags.length === 0) {
      toast({
        title: t('servicesManagement.form.therapyTagsRequired'),
        description: t('servicesManagement.form.therapyTagsRequiredDescription'),
        variant: "destructive"
      });
      return;
    }

    // Validate duration
    const totalMinutes = newService.duration_hours * 60 + newService.duration_minutes_part;
    if (totalMinutes < 15) {
      toast({
        title: t('servicesManagement.form.invalidDuration'),
        description: t('servicesManagement.form.invalidDurationDescription'),
        variant: "destructive"
      });
      return;
    }

    if (!validateForm(newService, selectedFormats)) {
      return;
    }

    const formats = Object.entries(selectedFormats)
      .filter(([_, selected]) => selected)
      .map(([format]) => format);

    const serviceData = {
      service_name: newService.service_name,
      description: newService.description,
      session_instructions: newService.session_instructions,
      price: Number(newService.price),
      duration_minutes: totalMinutes,
      formats_available: formats,
      currency: newService.currency,
      is_active: newService.is_active,
      is_free_session: newService.is_free_session,
      parental_consent_required: newService.parental_consent_required,
      adults_only: newService.adults_only,
      custom_therapy_tags: newService.custom_therapy_tags.length > 0 ? newService.custom_therapy_tags : null,
    };

    let success = false;
    let serviceId: string | null = null;

    if (editingService) {
      success = await updateService(editingService.id, serviceData);
      serviceId = editingService.id;
      if (success) {
        setEditingService(null);
      }
    } else {
      const result = await addService(serviceData);
      success = !!result;
      if (result) {
        serviceId = (result as any).id || null;
      }
    }
    
    if (success && serviceId) {
      // Handle therapy tags
      const { error: deleteError } = await supabase
        .from('service_therapy_tags')
        .delete()
        .eq('service_id', serviceId);

      if (!deleteError) {
        const therapyTagsData = newService.therapy_tags.map((therapyId, index) => ({
          service_id: serviceId,
          therapy_modality_id: therapyId,
          is_primary: index === 0,
        }));

        await supabase
          .from('service_therapy_tags')
          .insert(therapyTagsData);
      }

      // Refresh services to get complete data with tags
      await fetchServices();
      resetForm();
    }
  };

  return (
    <div className="space-y-6">
      <ServicesHeader servicesCount={services.length} />

      <ServicesContent
        services={services}
        isLoading={isLoading}
        onToggleActive={toggleServiceActive}
        onEdit={handleEdit}
        onDelete={deleteService}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{editingService ? 'Edit Service' : 'Add New Service'}</CardTitle>
              <CardDescription>
                {editingService ? 'Update your service details' : 'Create a new service to offer to your clients'}
              </CardDescription>
            </div>
            {editingService && (
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <NewServiceForm
            newService={newService}
            selectedFormats={selectedFormats}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onSelectChange={handleSelectChange}
            onFormatChange={handleFormatChange}
            onToggleActive={handleToggleActive}
            onToggleFreeSession={handleToggleFreeSession}
            onToggleParentalConsent={handleToggleParentalConsent}
            onToggleAdultsOnly={handleToggleAdultsOnly}
            onTherapyTagsChange={(tags) => handleSelectChange('therapy_tags', tags)}
            onCustomTagsChange={(tags) => handleSelectChange('custom_therapy_tags', tags)}
            onSubmit={handleSubmit}
          />
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Managing Services</AlertTitle>
        <AlertDescription>
          You can edit, activate/deactivate, or delete your services using the action buttons in the table above.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ServicesManager;